import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Button,
  LinearProgress,
  Link,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import cronstrue from 'cronstrue';
import { DateTime, DurationLikeObject } from 'luxon';
import {
  Content,
  ErrorBoundary,
  Header,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import type { BackupBlob } from '@internal/backstage-plugin-bidone-backend';
import {
  useReleaseChecklistBackups,
  useReleaseChecklistBackupInfo,
  useRunReleaseChecklistBackup,
  useDeleteReleaseChecklistBackups,
  useRestoreReleaseChecklistBackup,
} from '../../hooks/useReleaseChecklistBackups';
import { useReleaseFromHash } from '../../hooks/useReleaseFromHash';
import { ConfirmDialog } from '../ReleaseChecklist/ReleaseManagement/ConfirmDialog';
import {
  formatBytes,
  ReleaseChecklistBackupsTable,
} from './ReleaseChecklistBackupsTable';

// cronstrue throws on invalid expressions; fall back to a generic phrase
// so a misconfigured cron string never crashes the page.
const describeCron = (cron: string): string => {
  try {
    return cronstrue.toString(cron, { verbose: true }).toLowerCase();
  } catch {
    return 'on a custom schedule';
  }
};

// Build a short timezone abbreviation from the long IANA name. V8's `Intl`
// short timezone code returns "GMT+12" rather than "NZST" / "NZDT" /
// "AEST" etc., but the long name ("New Zealand Standard Time") reliably
// matches the "X Y Z Time" shape we can initial-extract from. Falls back
// to the GMT offset if the long name doesn't match the expected pattern.
const abbreviateZone = (dt: DateTime): string => {
  const long = dt.offsetNameLong;
  if (long) {
    const words = long.split(/\s+/).filter(Boolean);
    // Pattern: at least two words ending in "Time" — take all leading
    // initials including the T. "New Zealand Standard Time" → "NZST",
    // "Pacific Standard Time" → "PST", "British Summer Time" → "BST".
    if (words.length >= 2 && words[words.length - 1] === 'Time') {
      const initials = words.map(w => w[0]).join('');
      if (initials.length >= 2) return initials.toUpperCase();
    }
  }
  return dt.toFormat('ZZZZ');
};

// Convert a "minute hour * * *" daily UTC cron into a local-time description,
// e.g. "at 1:00 am NZST, every day". The timezone abbreviation is taken
// from the browser's current local offset, so the displayed time follows
// DST transitions automatically — same cron reads as `2:00 am NZDT` in
// summer and `1:00 am NZST` in winter. For non-daily expressions we
// can't shift cleanly (DOW/DOM would need to roll over too), so we fall
// back to a UTC-labelled description.
const describeCronLocal = (cron: string): string => {
  const dailyMatch = cron.trim().match(/^(\d+)\s+(\d+)\s+\*\s+\*\s+\*$/);
  if (dailyMatch) {
    const [, minStr, hourStr] = dailyMatch;
    const minute = Number(minStr);
    const hour = Number(hourStr);
    if (
      Number.isFinite(minute) &&
      Number.isFinite(hour) &&
      hour >= 0 &&
      hour < 24 &&
      minute >= 0 &&
      minute < 60
    ) {
      const local = DateTime.utc()
        .set({ hour, minute, second: 0, millisecond: 0 })
        .toLocal();
      const time = local.toFormat('h:mm a').toLowerCase();
      const zone = abbreviateZone(local);
      return `at ${time} ${zone}, every day`;
    }
  }
  return `${describeCron(cron)} UTC`;
};

// Builds the inline hint listing exactly which schedule fields are missing,
// e.g. "`enabled: true` and a cron value" or just "`enabled: true`". Keeps
// the disabled-schedule alert copy honest when only one of the two pieces
// is unset.
const scheduleMissingHint = (info: {
  scheduleEnabled: boolean;
  scheduleCron: string | null;
}): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  if (!info.scheduleEnabled) {
    parts.push(<code key="enabled">bidone.releaseChecklistBackup.enabled: true</code>);
  }
  if (!info.scheduleCron) {
    parts.push(<code key="cron">bidone.releaseChecklistBackup.cron</code>);
  }
  return parts.map((node, i) => (
    <React.Fragment key={i}>
      {i > 0 && ' and '}
      {node}
    </React.Fragment>
  ));
};

const InfoAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const Toolbar = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  flexWrap: 'wrap',
}));

const Spacer = styled('div')({ flex: 1 });

// The toolbar Delete button uses the same destructive style as the inline
// delete buttons in the Release Checklist sidebar. ConfirmDialog already
// applies its own danger styling via `variant: 'danger'`.
const DangerContainedButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  '&:hover': { backgroundColor: theme.palette.error.dark },
}));

// Usage thresholds for the quota progress-bar tint. The bar runs green up
// to 60%, switches to amber at 60%+, and turns red at 85%+ (or anywhere
// over 100%). Deliberately conservative so the warning surface kicks in
// well before the soft quota is actually hit.
type Tint = 'green' | 'amber' | 'red';
const usageColor = (percent: number): Tint => {
  if (percent >= 85) return 'red';
  if (percent >= 60) return 'amber';
  return 'green';
};

const TINT_PALETTE = {
  green: 'success',
  amber: 'warning',
  red: 'error',
} as const;

// Quota usage bar with per-tint track + fill colours. Drives both the
// inner track slot (`v5-MuiLinearProgress-colorPrimary`) and the fill
// slot (`v5-MuiLinearProgress-bar`) so the bar reads as "in the green /
// amber / red zone" even at very low usage where the fill is barely
// visible.
const QuotaProgress = styled(LinearProgress, {
  shouldForwardProp: prop => prop !== 'tint',
})<{ tint: Tint }>(({ theme, tint }) => {
  const palette = theme.palette[TINT_PALETTE[tint]];
  return {
    height: 8,
    borderRadius: 4,
    backgroundColor: palette.light,
    '& .v5-MuiLinearProgress-bar': {
      backgroundColor: palette.main,
    },
  };
});

const QuotaRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(0.5),
}));

// Bulk-select presets for the "Select older than" dropdown. Each option
// selects every backup whose `takenAt` is strictly older than (now − duration).
const AGE_OPTIONS: ReadonlyArray<{
  label: string;
  duration: DurationLikeObject;
}> = [
  { label: '1 week', duration: { weeks: 1 } },
  { label: '1 month', duration: { months: 1 } },
  { label: '3 months', duration: { months: 3 } },
  { label: '6 months', duration: { months: 6 } },
];

const BackupsFetch: React.FC = () => {
  const alertApi = useApi(alertApiRef);
  const { data, isFetching, error } = useReleaseChecklistBackups();
  const { data: info } = useReleaseChecklistBackupInfo();
  const runBackup = useRunReleaseChecklistBackup();
  const deleteBackups = useDeleteReleaseChecklistBackups();
  const restoreBackup = useRestoreReleaseChecklistBackup();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<BackupBlob | null>(null);
  const [ageMenuAnchor, setAgeMenuAnchor] = useState<null | HTMLElement>(null);
  // Track which release groups are expanded. Seeded from the URL hash so
  // arriving from /release-checklist#?release=<r> opens that group, while
  // a bare /release-checklist-backups visit keeps everything collapsed.
  const hashRelease = useReleaseFromHash();
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    hashRelease ? new Set([hashRelease]) : new Set(),
  );

  // Re-open whichever release the hash currently points at after a
  // back/forward nav or external link paste. Doesn't collapse already-open
  // groups — user toggles aren't fighting the URL.
  useEffect(() => {
    if (!hashRelease) return;
    setExpanded(prev => {
      if (prev.has(hashRelease)) return prev;
      const out = new Set(prev);
      out.add(hashRelease);
      return out;
    });
  }, [hashRelease]);

  const toggleExpanded = (release: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(release)) next.delete(release);
      else next.add(release);
      return next;
    });
  };

  const rows = data ?? [];
  const visibleNames = useMemo(() => rows.map(r => r.blobName), [rows]);
  const totalBytes = useMemo(
    () => rows.reduce((acc, r) => acc + (r.sizeBytes || 0), 0),
    [rows],
  );
  const releaseCount = useMemo(
    () => new Set(rows.map(r => r.release)).size,
    [rows],
  );
  const quotaBytes = info?.quotaBytes ?? 0;
  const quotaPercent = quotaBytes > 0 ? (totalBytes / quotaBytes) * 100 : 0;
  const quotaTint = usageColor(quotaPercent);

  // If a row is removed from under us (e.g. after a delete refetch), drop
  // any stale entries from the selection set rather than acting on names
  // that no longer exist.
  useEffect(() => {
    if (selected.size === 0) return;
    const allowed = new Set(visibleNames);
    const filtered = Array.from(selected).filter(n => allowed.has(n));
    if (filtered.length !== selected.size) setSelected(new Set(filtered));
  }, [visibleNames, selected]);

  const toggle = (blobName: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(blobName)) next.delete(blobName);
      else next.add(blobName);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(prev =>
      prev.size === rows.length ? new Set() : new Set(visibleNames),
    );
  };

  const toggleMany = (blobNames: string[], next: boolean) => {
    setSelected(prev => {
      const out = new Set(prev);
      for (const name of blobNames) {
        if (next) out.add(name);
        else out.delete(name);
      }
      return out;
    });
  };

  // Bulk-select every backup older than the chosen age. Replaces the current
  // selection so the result is exactly the matching rows, and auto-expands
  // the affected release groups so the user can review what's checked before
  // hitting "Delete selected". Rows with an unparseable `takenAt` are skipped.
  const selectOlderThan = (duration: DurationLikeObject, label: string) => {
    setAgeMenuAnchor(null);
    const cutoff = DateTime.utc().minus(duration).toMillis();
    const matching = rows.filter(r => {
      if (!r.takenAt) return false;
      const millis = DateTime.fromISO(r.takenAt, { zone: 'utc' }).toMillis();
      return Number.isFinite(millis) && millis < cutoff;
    });
    setSelected(new Set(matching.map(r => r.blobName)));
    setExpanded(prev => {
      const out = new Set(prev);
      for (const r of matching) out.add(r.release);
      return out;
    });
    alertApi.post({
      message:
        matching.length === 0
          ? `No backups older than ${label}`
          : `Selected ${matching.length} backup${
              matching.length === 1 ? '' : 's'
            } older than ${label}`,
      severity: matching.length === 0 ? 'info' : 'success',
    });
  };

  const handleRun = async () => {
    try {
      const result = await runBackup.mutateAsync();
      alertApi.post({
        message: `Backup complete: ${result.partitions} partition${
          result.partitions === 1 ? '' : 's'
        }, ${result.bytes} byte${result.bytes === 1 ? '' : 's'}`,
        severity: 'success',
      });
    } catch (e) {
      alertApi.post({
        message: `Backup failed: ${(e as Error).message}`,
        severity: 'error',
      });
    }
  };

  const handleDeleteSelected = async () => {
    const blobNames = Array.from(selected);
    if (blobNames.length === 0) return;
    try {
      const result = await deleteBackups.mutateAsync({ blobNames });
      setSelected(new Set());
      setConfirmDeleteOpen(false);
      alertApi.post({
        message: `Deleted ${result.deleted} backup${
          result.deleted === 1 ? '' : 's'
        }`,
        severity: 'success',
      });
    } catch (e) {
      setConfirmDeleteOpen(false);
      alertApi.post({
        message: `Failed to delete backups: ${(e as Error).message}`,
        severity: 'error',
      });
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    const target = restoreTarget;
    try {
      const result = await restoreBackup.mutateAsync({
        release: target.release,
        blobName: target.blobName,
      });
      setRestoreTarget(null);
      alertApi.post({
        message: `Restored ${result.release} (${result.items} row${
          result.items === 1 ? '' : 's'
        })`,
        severity: 'success',
      });
    } catch (e) {
      setRestoreTarget(null);
      alertApi.post({
        message: `Failed to restore backup: ${(e as Error).message}`,
        severity: 'error',
      });
    }
  };

  // Preserve the current hash on the round-trip back to /release-checklist
  // so the user lands on the same release they came from.
  const backToReleasesHref = `/release-checklist${
    typeof window !== 'undefined' ? window.location.hash : ''
  }`;

  if (isFetching && !data) return <Progress />;
  if (error) {
    return (
      <ResponseErrorPanel
        error={error as Error}
        title="Fetching backups"
        defaultExpanded
      />
    );
  }

  // Compose the "stored at" label once so the card stays as either a
  // linked region-env tag (when the backend resolved a portal URL) or a
  // plain text fragment (when the config is incomplete).
  const locationLabel = info ? `${info.region}-${info.env}` : null;
  const locationNode = info?.portalUrl ? (
    <Link
      href={info.portalUrl}
      target="_blank"
      rel="noopener noreferrer"
      color="primary"
    >
      {locationLabel}
    </Link>
  ) : (
    <strong>{locationLabel}</strong>
  );

  return (
    <>
      <Toolbar>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to={backToReleasesHref}
        >
          Back to releases
        </Button>
        <Spacer />
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleRun}
          disabled={runBackup.isPending}
        >
          {runBackup.isPending ? 'Running…' : 'Run backup now'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          endIcon={<ArrowDropDownIcon />}
          onClick={e => setAgeMenuAnchor(e.currentTarget)}
          disabled={rows.length === 0}
          aria-haspopup="menu"
          aria-expanded={ageMenuAnchor ? 'true' : undefined}
        >
          Select older than
        </Button>
        <Menu
          anchorEl={ageMenuAnchor}
          open={!!ageMenuAnchor}
          onClose={() => setAgeMenuAnchor(null)}
        >
          {AGE_OPTIONS.map(opt => (
            <MenuItem
              key={opt.label}
              onClick={() => selectOlderThan(opt.duration, opt.label)}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Menu>
        <DangerContainedButton
          variant="contained"
          size="small"
          disabled={selected.size === 0 || deleteBackups.isPending}
          onClick={() => setConfirmDeleteOpen(true)}
        >
          {deleteBackups.isPending
            ? 'Deleting…'
            : `Delete selected${selected.size > 0 ? ` (${selected.size})` : ''}`}
        </DangerContainedButton>
      </Toolbar>

      {info && (
        <>
          <InfoAlert severity="info" variant="outlined">
            Backups are stored at {locationNode} (container{' '}
            <code>{info.container}</code>).
            {info.portalUrlMissing.length > 0 && (
              <>
                {' '}
                Portal link disabled — set{' '}
                {info.portalUrlMissing.map((path, i) => (
                  <React.Fragment key={path}>
                    {i > 0 && ', '}
                    <code>{path}</code>
                  </React.Fragment>
                ))}{' '}
                in <code>app-config.local.yaml</code> to enable it.
              </>
            )}
          </InfoAlert>
          {info.scheduleEnabled && info.scheduleCron ? (
            <InfoAlert severity="info" variant="outlined">
              Scheduled backups run{' '}
              <strong>{describeCronLocal(info.scheduleCron)}</strong> (cron{' '}
              <code>{info.scheduleCron}</code> UTC). Set{' '}
              <code>bidone.releaseChecklistBackup.cron</code> in{' '}
              <code>app-config.yaml</code> to change the cadence.
            </InfoAlert>
          ) : (
            <InfoAlert severity="warning" variant="outlined">
              Scheduled backups are <strong>disabled</strong> — only manual
              "Run backup now" clicks will produce snapshots. Set{' '}
              {scheduleMissingHint(info)} in <code>app-config.yaml</code> to
              schedule.
            </InfoAlert>
          )}
        </>
      )}

      {rows.length > 0 && (
        <InfoAlert severity="info" variant="outlined" icon={false}>
          <strong>
            Total: {rows.length} backup{rows.length === 1 ? '' : 's'} across{' '}
            {releaseCount} release{releaseCount === 1 ? '' : 's'}
          </strong>{' '}
          — <strong>{formatBytes(totalBytes)}</strong>
          {quotaBytes > 0 && (
            <QuotaRow>
              <QuotaProgress
                variant="determinate"
                value={Math.min(quotaPercent, 100)}
                tint={quotaTint}
                style={{ flex: 1 }}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ whiteSpace: 'nowrap' }}
              >
                {formatBytes(totalBytes)} / {formatBytes(quotaBytes)} (
                {quotaPercent.toFixed(1)}%)
              </Typography>
            </QuotaRow>
          )}
        </InfoAlert>
      )}

      <ReleaseChecklistBackupsTable
        rows={rows}
        selected={selected}
        expanded={expanded}
        onToggle={toggle}
        onToggleAll={toggleAll}
        onToggleMany={toggleMany}
        onToggleExpanded={toggleExpanded}
        onRestore={setRestoreTarget}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete selected backups?"
        body={
          <>
            Permanently delete <strong>{selected.size}</strong> backup
            {selected.size === 1 ? '' : 's'}? This removes the zip blob
            {selected.size === 1 ? '' : 's'} from storage and can't be undone.
          </>
        }
        confirmLabel="Delete"
        confirmPendingLabel="Deleting…"
        pending={deleteBackups.isPending}
        variant="danger"
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteSelected}
      />

      <ConfirmDialog
        open={!!restoreTarget}
        title="Restore release from backup?"
        body={
          <>
            Restore <strong>{restoreTarget?.release}</strong> from the snapshot
            taken at{' '}
            <strong>
              {restoreTarget?.takenAt
                ? new Date(restoreTarget.takenAt).toLocaleString()
                : ''}
            </strong>
            ? The current live checklist for this release will be wiped and
            replaced with the backup contents.
          </>
        }
        confirmLabel="Restore"
        confirmPendingLabel="Restoring…"
        pending={restoreBackup.isPending}
        variant="primary"
        onClose={() => setRestoreTarget(null)}
        onConfirm={handleRestore}
      />
    </>
  );
};

/** @public */
export const ReleaseChecklistBackupsPage = () => (
  <Page themeId="tool">
    <Header
      title="Release Checklist Backups"
      subtitle="Scheduled snapshots of release checklists"
    />
    <Content>
      <ErrorBoundary>
        <BackupsFetch />
      </ErrorBoundary>
    </Content>
  </Page>
);
